/* import __COLOCATED_TEMPLATE__ from './edit-channel-limit.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { post } from 'embercom/lib/ajax';
import ENV from 'embercom/config/environment';
import type IntlService from 'embercom/services/intl';
import type AppConversationCapacityLimit from 'embercom/models/app-conversation-capacity-limit';
// @ts-expect-error
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';

type ChannelType = 'email' | 'messenger';

interface Signature {
  Args: {
    teammates: any[];
    workspaceSettings: AppConversationCapacityLimit;
    bulkUpdateLimit: number;
    channelType: ChannelType;
  };
}

export default class EditChannelLimit extends Component<Signature> {
  @service declare appService: AppService;
  @service declare notificationsService: any;
  @service declare intl: IntlService;
  @service declare permissionsService: any;
  @service declare intercomEventService: any;
  @service declare intercomConfirmService: any;
  @service declare modelDataCacheService: any;

  @tracked channelLimit = '';
  @tracked selectedTeammates: any[] = [];
  @tracked valueIsModified = false;
  @tracked showUpdateNumber = false;
  @tracked isReverting = false;

  get workspaceChannelLimit(): number | undefined {
    return (
      this.args.workspaceSettings?.channelLimits?.[this.args.channelType] ??
      this.args.workspaceSettings?.capacityLimit
    );
  }

  get editLabel(): string {
    if (this.args.channelType === 'email') {
      return this.intl.t('settings.teammate-workload.channel-limit.email-edit-label');
    }
    return this.intl.t('settings.teammate-workload.channel-limit.messenger-edit-label');
  }

  get cantChangeChannelLimit(): boolean {
    return !this.permissionsService.currentAdminCan('can_manage_workload_management');
  }

  get channelLimitInvalid(): boolean {
    let value = Number(this.channelLimit);
    return !this.channelLimit || value > 1_000_000 || value < 0 || !Number.isInteger(value);
  }

  get bulkUpdateAllowed(): boolean {
    let selectedCount = this.args.teammates.filter((t) => t.isSelected).length;
    return selectedCount <= this.args.bulkUpdateLimit;
  }

  @action
  popoverClosed() {
    this.showUpdateNumber = false;
  }

  @action
  prepareUpdate() {
    this.resetState();
    this.selectedTeammates = this.args.teammates.filter((t) => t.isSelected);
  }

  @action
  async pushUpdates() {
    if (!this.isReverting && this.channelLimitInvalid) {
      return;
    }

    if (!(await this.askConfirmation())) {
      this.isReverting = false;
      return;
    }

    try {
      let teammates = this.selectedTeammates.map((t) => t.id);
      this.notificationsService.notifyConfirmation(
        this.intl.t('settings.teammate-workload.channel-limit.updating', {
          count: teammates.length,
        }),
        ENV.APP._1M * 60,
      );

      let newLimitValue = this.isReverting ? null : Number(this.channelLimit);

      let channelLimits: Record<string, number | null> = this.isReverting
        ? { [this.args.channelType]: null }
        : { [this.args.channelType]: newLimitValue as number };

      await post('/ember/admins/change_channel_limits', {
        channel_limits: channelLimits,
        admin_ids: teammates,
        id: this.appService.app.currentAdmin.id,
        app_id: this.appService.app.id,
      });

      this.selectedTeammates.forEach((teammate) => {
        let existingLimits = { ...(teammate.workload_management_settings?.channel_limits ?? {}) };
        if (this.isReverting) {
          delete existingLimits[this.args.channelType];
        } else {
          existingLimits[this.args.channelType] = newLimitValue as number;
        }
        teammate.workload_management_settings = {
          ...teammate.workload_management_settings,
          channel_limits: existingLimits,
        };
      });

      this.notificationsService.clear();
      this.notificationsService.notifyConfirmation(
        this.intl.t('settings.teammate-workload.channel-limit.updated', {
          count: teammates.length,
        }),
      );

      this.intercomEventService.trackAnalyticsEvent({
        action: 'updated',
        object: 'channel_limit',
        place: 'teammate_workload',
        teammate_ids: teammates,
        selected_teammate_count: teammates.length,
        channel_type: this.args.channelType,
        channel_limit: newLimitValue,
      });

      this.resetState();
    } catch (e: unknown) {
      this.notificationsService.clear();
      if (e instanceof Object && 'jqXHR' in e) {
        let ajaxError = e as { jqXHR: { responseJSON?: { errors?: string } } };
        this.notificationsService.notifyError(ajaxError.jqXHR.responseJSON?.errors);
      }
    } finally {
      this.appService.app.updateLocalCache(this.modelDataCacheService);
    }
  }

  private async askConfirmation(): Promise<boolean> {
    return await this.intercomConfirmService.confirm({
      title: this.intl.t('settings.teammate-workload.channel-limit.confirmation.title'),
      body: sanitizeHtml(
        this.isReverting
          ? this.intl.t('settings.teammate-workload.channel-limit.confirmation.body-revert', {
              workspaceLimit: this.workspaceChannelLimit,
              teammateCount: this.selectedTeammates.length,
            })
          : this.intl.t('settings.teammate-workload.channel-limit.confirmation.body', {
              channelLimit: this.channelLimit,
              teammateCount: this.selectedTeammates.length,
            }),
      ),
      cancelButtonText: this.intl.t('settings.teammate-workload.channel-limit.confirmation.cancel'),
      confirmButtonText: this.intl.t(
        'settings.teammate-workload.channel-limit.confirmation.confirm',
      ),
    });
  }

  private resetState() {
    this.channelLimit = '';
    this.valueIsModified = false;
    this.isReverting = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox::TeammateWorkload::BulkActions::EditChannelLimit': typeof EditChannelLimit;
    'inbox/teammate-workload/bulk-actions/edit-channel-limit': typeof EditChannelLimit;
  }
}
